import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { camelCase, find, isEmpty } from "lodash";
import {
  useDealStages,
  useDeleteDealStage,
  useStageById
} from "@/entities/settings/deal/hooks";
import Section from "@/pages/settings/components/sections/Section";
import { Button } from "@/components/ui/button";
import { SettingItemLayoutProps } from "@/pages/settings/components/settingsItems/SettingItemLayout";
import { DealMilestone, DealStage } from "@/pages/deals/types";
import IconContainer from "@/components/ui/icon-container";
import { getColorForMilestoneByType } from "@/components/shared/Milestones";
import { AlertBubbleWarning, Polygon } from "@dreamhub/designsystem/icons";
import Text from "@/components/ui/text";
import EmptyQuestionnaires from "@/pages/settings/components/dealsSettings/StageStudio/EmptyQuestionnaires";
import LogoIconContainer from "@/components/ui/logo-icon-container";
import Banner from "@/components/ui/banner";
import SettingsNavigation from "@/pages/settings/components/SettingsNavigation";
import Skeleton from "@/components/ui/skeleton";
import DeleteStageModal from "@/pages/settings/components/shared/stageStudio/DeleteStageModal";
import EditStageDrawer from "@/pages/settings/components/dealsSettings/StageStudio/EditStageDrawer";
import DraggableWorkflowListContainer from "@/pages/settings/components/dealsSettings/StageStudio/workflow/DraggableWorkflowListContainer";
import toast from "react-hot-toast";
import { WorkflowItem } from "@/pages/settings/components/dealsSettings/StageStudio/workflow/DraggableWorkflowItem";
import AddActionButton from "@/components/ui/add-action-button";
import AddWorkflowModal from "@/pages/settings/components/dealsSettings/StageStudio/addWorkflow/AddWorkflowModal";

const TRANSLATION_PREFIX = "settings.deals.stageStudio.stageDetailedView";

export const getMilestoneIcon = (
  milestoneId: DealMilestone | string | number
) => {
  return (
    <IconContainer
      size="sm"
      style={{
        color: getColorForMilestoneByType(
          milestoneId as DealMilestone,
          "border"
        )
      }}
    >
      <Polygon />
    </IconContainer>
  );
};

const StageDetailedView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { stageId } = useParams<{ stageId: string }>();

  const { data: stage, isLoading } = useStageById(
    stageId || "",
    !isEmpty(stageId)
  );

  useEffect(() => {
    queryClient.invalidateQueries({
      queryKey: ["settings/deals/stages/stageById"]
    });
    queryClient.removeQueries({ queryKey: ["questionnaires/byId"] });
  }, [queryClient]);

  const { data: dealStagesData } = useDealStages();
  const stages = dealStagesData?.stages || [];

  const deleteStage = useDeleteDealStage();

  const [isDeleteStageModalOpen, setIsDeleteStageModalOpen] = useState(false);
  const [isEditStageDrawerOpen, setIsEditStageDrawerOpen] = useState(false);
  const [isAddWorkflowModalOpen, setIsAddWorkflowModalOpen] = useState(false);

  const [questionnaires, setQuestionnaires] = useState<WorkflowItem[]>([]);

  useEffect(() => {
    if (stage?.questionnaires) {
      setQuestionnaires(stage.questionnaires);
    }
  }, [stage?.questionnaires]);

  const handleQuestionnaireOrderChange = (newItems: WorkflowItem[]) => {
    setQuestionnaires(newItems);
  };

  const getTargetStageName = () => {
    const currentStageOrder = stage?.stageOrder || 1;
    let targetStageOrder = currentStageOrder + 1;
    // if it's the last stage, the target stage is the previous one
    if (currentStageOrder === stages?.length) {
      targetStageOrder = currentStageOrder - 1;
    }
    const targetStage = find(
      stages,
      (st) => st.stageOrder === targetStageOrder
    );
    return targetStage ? targetStage?.name : "";
  };

  const handleDeleteStage = () => {
    deleteStage.mutate(
      {
        pathParams: {
          stageId: stageId || ""
        }
      },
      {
        onSuccess: () => {
          setIsDeleteStageModalOpen(false);
          navigate("/settings/deals?tab=stageStudio");
          queryClient.invalidateQueries({
            queryKey: ["settings/deals/stages"]
          });
          toast.success(
            t(`${TRANSLATION_PREFIX}.deleteStageModal.successToast`, {
              stageName: stage?.name
            }),
            {
              duration: 5000
            }
          );
        }
      }
    );
  };

  return (
    <>
      <div className="flex w-full items-center justify-center">
        <div className="w-full max-w-[800px] flex flex-col gap-12 pt-6 pb-12">
          <div className="flex flex-col gap-10">
            <SettingsNavigation
              isLoading={isLoading}
              items={[
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.dealSettings`),
                  to: "/settings/deals"
                },
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.stageStudio`),
                  to: "/settings/deals?tab=stageStudio"
                },
                {
                  label: t(`${TRANSLATION_PREFIX}.navigation.stageName`, {
                    stage: stage?.name
                  }),
                  isActive: true
                }
              ]}
            />
            {isLoading ? (
              <Skeleton className="h-7 w-[15rem]" />
            ) : (
              <div className="flex gap-4 items-center">
                <div
                  className="block w-2 rounded-[28px] h-[52px]"
                  style={{
                    backgroundColor: getColorForMilestoneByType(
                      stage?.milestone as DealMilestone,
                      "border"
                    )
                  }}
                />
                <div className="flex flex-col">
                  <Text variant="title3H1">{stage?.name}</Text>
                  <Text variant="bodySecondary" className="text-secondary">
                    {t(`${TRANSLATION_PREFIX}.stageLabel`)}
                  </Text>
                </div>
              </div>
            )}
            {!isLoading && isEmpty(questionnaires) && (
              <Banner
                variant="general"
                title={t(`${TRANSLATION_PREFIX}.bannerText`)}
                buttonText={t(`${TRANSLATION_PREFIX}.bannerAction`)}
                onButtonClick={() => setIsAddWorkflowModalOpen(true)}
                showCloseButton
              />
            )}
          </div>
          <Section
            isLoading={isLoading}
            title={t(`${TRANSLATION_PREFIX}.details`)}
            action={
              <Button
                variant="tertiary"
                onClick={() => setIsEditStageDrawerOpen(true)}
                disabled={isLoading}
              >
                {t("edit")}
              </Button>
            }
            items={
              [
                {
                  title: t(`${TRANSLATION_PREFIX}.name`),
                  textValue: stage?.name,
                  className: "p-4",
                  titleClassName: "text-secondary"
                },
                //   TODO: not supported for now
                //   {
                //     title: "Stage description",
                //     textValue:
                //       "Confirm that the pains our solution addresses are meaningful, create a clear negative impact, and have budget (or willingness) behind them. We also make sure these pains directly affect our contacts, motivating them to move the deal forward.",
                //     className: "p-4",
                //     titleClassName: "text-secondary",
                //   },
                {
                  title: t(`${TRANSLATION_PREFIX}.milestone`),
                  textValue: (
                    <div className="flex gap-2 items-center">
                      {getMilestoneIcon((stage as DealStage)?.milestone)}
                      <Text>
                        {t(
                          `deals.dealView.milestone.${camelCase(DealMilestone[(stage as DealStage)?.milestone])}`
                        )}
                      </Text>
                    </div>
                  ),
                  className: "p-4",
                  titleClassName: "text-secondary"
                }
              ] as SettingItemLayoutProps[]
            }
          />
          <Section
            isLoading={isLoading}
            title={t(`${TRANSLATION_PREFIX}.workflows`)}
            subTitle={t(`${TRANSLATION_PREFIX}.workflowsSubtitle`)}
            action={
              questionnaires.length > 0 ? (
                <AddActionButton
                  label="Add workflow"
                  onClick={() => setIsAddWorkflowModalOpen(true)}
                />
              ) : undefined
            }
          >
            {isEmpty(questionnaires) ? (
              <EmptyQuestionnaires
                tPrefix={TRANSLATION_PREFIX}
                onButtonClick={() => setIsAddWorkflowModalOpen(true)}
              />
            ) : (
              <DraggableWorkflowListContainer
                items={questionnaires}
                isDragDisabled={false}
                stageId={stageId}
                onItemsChange={handleQuestionnaireOrderChange}
              />
            )}
          </Section>
          {!isLoading && (
            <Section
              items={
                [
                  {
                    title: t(`${TRANSLATION_PREFIX}.dangerZone`),
                    subTitle: t(`${TRANSLATION_PREFIX}.dangerZoneSubtitle`),
                    icon: (
                      <LogoIconContainer
                        className="size-10"
                        icon={<AlertBubbleWarning />}
                        variant="danger"
                      />
                    ),
                    actionType: "button",
                    actionProps: {
                      variant: "tertiary",
                      destructive: true,
                      onClick: () => setIsDeleteStageModalOpen(true),
                      label: t(`${TRANSLATION_PREFIX}.deleteStage`)
                    }
                  }
                ] as SettingItemLayoutProps[]
              }
            />
          )}
        </div>
      </div>
      <DeleteStageModal
        isOpen={isDeleteStageModalOpen}
        setIsOpen={setIsDeleteStageModalOpen}
        handleDelete={handleDeleteStage}
        stageName={stage?.name || ""}
        count={stage?.dealsCount || 0}
        targetStageName={getTargetStageName()}
        tPrefix={`${TRANSLATION_PREFIX}.deleteStageModal`}
      />
      <EditStageDrawer
        isOpen={isEditStageDrawerOpen}
        onClose={(stageWasUpdated) => {
          setIsEditStageDrawerOpen(false);
          if (stageWasUpdated) {
            // Refresh the current stage data
            queryClient.invalidateQueries({
              queryKey: ["settings/deals/stages/stageById"]
            });
          }
        }}
        stageData={stage || null}
      />
      <AddWorkflowModal
        isOpen={isAddWorkflowModalOpen}
        setIsOpen={setIsAddWorkflowModalOpen}
        stageId={stageId}
      />
    </>
  );
};

export default StageDetailedView;
